import React from "react";
import { useLocation } from "react-router-dom";
import { API_URL } from "../config/constants";
import { getAuthToken } from "./authToken";

export function timeout(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function authHeader() {
    const token = getAuthToken();
    if (token) {
        return { Authorization: 'Bearer ' + token };
    }
    return {};
}

/**
 * Normalizes fetch responses. On failure: throws Error with .status / .body, logs to console.
 * Accepts either a Response or a Promise<Response>; translates raw network failures
 * (Chrome's "Failed to fetch", Safari's "Load failed", etc.) into a friendlier message.
 */
export const handleResponse = async (response, onError) => {
    let res;
    try {
        res = await response;
    } catch (networkErr) {
        const friendly =
            "Couldn't reach the ATO server. Check your internet connection or any ad/privacy blockers, then try again.";
        const err = new Error(friendly);
        err.status = 0;
        err.cause = networkErr;
        console.error('[API network error]', networkErr && networkErr.message ? networkErr.message : networkErr);
        throw err;
    }
    const text = await res.text();

    let data = null;
    if (text) {
        try {
            data = JSON.parse(text);
        } catch {
            data = { message: text.slice(0, 800) };
        }
    }

    if (!res.ok) {
        if (res.status === 401 && onError) {
            onError();
        }

        const message =
            (data && (data.message || (typeof data.error === 'string' ? data.error : null))) ||
            res.statusText ||
            'Request failed';

        const err = new Error(message);
        err.status = res.status;
        err.body = data;

        console.error('[API error]', res.url || '(no url)', res.status, message, data ?? text);

        throw err;
    }

    return data;
};

export const getImagePath = (file) => {
    return API_URL + '/uploads/' + file.path;
}

export const getResolvedPath = (file) => {
    try {
        if (!file || !file.path || String(file.path).trim() === '') return '';
        if (/^https?:\/\//i.test(file.path)) return file.path;
        const pathSeg = encodeURIComponent(String(file.path));
        if (file.name) {
            return `${API_URL}/files/${pathSeg}/${encodeURIComponent(String(file.name))}`;
        }
        return `${API_URL}/files/${pathSeg}`;
    } catch (err) {
        console.log(err);
        return '';
    }
}

export const getAttachPath = (file) => {
    try {
        return getResolvedPath(file);
    } catch (err) {
        console.log(err);
        return '';
    }
}

export const checkIsAdmin = (user) => {
    const admin_list = (process.env.REACT_APP_ADMIN_USERS || "").split(",").filter(Boolean);
    return admin_list.indexOf(user) >= 0;
    
}

export const useQuery = () => {
    const { search } = useLocation();
  
    return React.useMemo(() => new URLSearchParams(search), [search]);
}

export const getArtworkQuery = (artwork, artist) => {
    const slug = (s) => (s || 'unknown').toLowerCase().replace(/[^A-Z0-9]/ig, '-');
    const name = slug(artwork?.name);
    const first = slug(artist?.firstname);
    const last = slug(artist?.lastname);
    return `${name}-${first}-${last}`;
}

export const getArtworkThumbnail = (artwork) => {
    const images = artwork?.images || [];
    const first = images.find((img) => img && img.path && String(img.path).trim() !== '');
    return first ? getResolvedPath(first) : '';
}

export const short_string = (text) => {
    return text.length > 50?(text.substr(0,50)+'...'):text;
}